import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAutoRefresh, useCartChanges } from "@/hooks/useAutoRefresh";
import { toast } from "sonner";

// Sepet ↔ hazırlık listesi canlı durumu + mesajlar. Sepet sayacı (c) ve genel sayaç (v) değişince anında yenilenir.
export function useCartLive(cartId, { userId, onIncoming } = {}) {
  const [live, setLive] = useState(null);
  const seen = useRef(new Set());
  const first = useRef(true);
  const cb = useRef(onIncoming); cb.current = onIncoming;
  const idRef = useRef(cartId); idRef.current = cartId;

  const load = async () => {
    const id = idRef.current;
    if (!id) return;
    try {
      const { data } = await api.get(`/pos-carts/${id}/live`);
      if (idRef.current !== id) return;
      const fresh = (data.messages || []).filter((m) => !seen.current.has(m.id));
      fresh.forEach((m) => seen.current.add(m.id));
      if (!first.current) fresh.filter((m) => m.from_id !== userId).forEach((m) => cb.current && cb.current(m));
      first.current = false;
      setLive(data);
    } catch { /* ağ hatası — sonraki sinyalde tekrar */ }
  };
  useEffect(() => { seen.current = new Set(); first.current = true; setLive(null); if (cartId) load(); }, [cartId]); // eslint-disable-line
  useCartChanges(() => { if (idRef.current) load(); });
  useAutoRefresh(() => { if (idRef.current) load(); });

  const send = async (payload) => {
    try {
      const { data } = await api.post(`/pos-carts/${cartId}/messages`, payload);
      seen.current.add(data.id);
      setLive((p) => (p ? { ...p, messages: [...(p.messages || []), data] } : p));
      return true;
    } catch (e) { toast.error(apiError(e)); return false; }
  };
  const markRead = async () => {
    if (!cartId || !live?.unread) return;
    setLive((p) => (p ? { ...p, unread: 0 } : p));
    try { await api.post(`/pos-carts/${cartId}/messages/read`); } catch { /* sonraki açılışta tekrar */ }
  };
  const patchMsg = (id, patch) => setLive((p) => (p ? { ...p, messages: (p.messages || []).map((m) => (m.id === id ? { ...m, ...patch } : m)) } : p));
  const edit = async (id, text) => {
    try {
      const { data } = await api.put(`/pos-carts/${cartId}/messages/${id}`, { text });
      patchMsg(id, { text: data.text, edited_at: data.edited_at });
      return true;
    } catch (e) { toast.error(apiError(e)); return false; }
  };
  const remove = async (id) => {
    try {
      const { data } = await api.delete(`/pos-carts/${cartId}/messages/${id}`);
      patchMsg(id, { deleted: true, deleted_at: data.deleted_at, deleted_by: data.deleted_by, text: "", photo_url: "", preset: "", product_name: "" });
      return true;
    } catch (e) { toast.error(apiError(e)); return false; }
  };
  return { live, unread: live?.unread || 0, prep: live?.prep || null, messages: live?.messages || [], reload: load, send, edit, remove, markRead };
}

// Sekme başlığı yanıp söner: "(2) Yeni mesaj · Gümüşalan Elektrik Panel" (birden çok kaynak toplanır)
const BASE_TITLE = "Gümüşalan Elektrik Panel";
const flashSrc = new Map();
let flashTimer = null;
const flashTick = (() => { let on = false; return () => {
  const n = [...flashSrc.values()].reduce((a, b) => a + b, 0);
  if (!n) { clearInterval(flashTimer); flashTimer = null; document.title = BASE_TITLE; return; }
  on = !on;
  document.title = on ? `(${n}) Yeni mesaj · ${BASE_TITLE}` : `● ${BASE_TITLE}`;
}; })();

export function useTitleFlash(key, count) {
  useEffect(() => {
    if (count > 0) flashSrc.set(key, count); else flashSrc.delete(key);
    if (flashSrc.size && !flashTimer) flashTimer = setInterval(flashTick, 1100);
    if (!flashSrc.size) flashTick();
    return () => { flashSrc.delete(key); if (!flashSrc.size) flashTick(); };
  }, [key, count]);
}

// Zil bildirimi aynı mesajı ikinci kez göstermesin (sayfa zaten uyardı)
export const silenceBell = (type) => window.dispatchEvent(new CustomEvent("ge:notifications-poll", { detail: { silentType: type } }));
